import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth/services/auth.service';
import { SubdomainService } from '../../../core/services/subdomain.service';
import { PatrolUserService } from '../../user/service/patrol-user.service';
import {
  PATROL_ENABLED_SOURCES_PREF_KEY,
  parsePatrolEnabledSources,
  serializePatrolEnabledSources,
} from '../constants/patrol-enabled-sources.constants';
import { PatrolCommunitySourceId } from '../models/patrol-community-source.model';
import { PatrolPlatformFlagsService } from './patrol-platform-flags.service';

@Injectable({ providedIn: 'root' })
export class PatrolCommunitySourcesService {
  #http = inject(HttpClient);
  #authService = inject(AuthService);
  #subdomain = inject(SubdomainService);
  #patrolUserService = inject(PatrolUserService);
  #queryClient = inject(QueryClient);
  #platformFlags = inject(PatrolPlatformFlagsService);

  readonly #enabled = signal<ReadonlySet<PatrolCommunitySourceId>>(new Set());
  readonly #profileReady = signal(false);

  readonly enabled = this.#enabled.asReadonly();

  constructor() {
    effect(() => {
      if (
        !this.#authService.isAuthenticated() ||
        !this.#subdomain.isPatrolSubdomain()
      ) {
        this.#enabled.set(new Set());
        this.#profileReady.set(false);
        return;
      }

      if (this.#patrolUserService.isPending()) {
        return;
      }

      const user = this.#patrolUserService.patrolUser();
      if (!user) {
        this.#profileReady.set(false);
        return;
      }

      this.#applyPreferences(user.settings?.preferences);
      this.#profileReady.set(true);
    });
  }

  isGloballyEnabled(id: PatrolCommunitySourceId): boolean {
    return this.#platformFlags.isGloballyEnabled(id);
  }

  isEnabled(id: PatrolCommunitySourceId): boolean {
    if (!this.isGloballyEnabled(id)) {
      return false;
    }
    return this.#enabled().has(id);
  }

  async whenReady(): Promise<void> {
    if (
      !this.#authService.isAuthenticated() ||
      !this.#subdomain.isPatrolSubdomain()
    ) {
      return;
    }

    await this.#platformFlags.whenReady();
    await this.#ensurePatrolProfileLoaded();

    const user = this.#patrolUserService.patrolUser();
    this.#applyPreferences(user?.settings?.preferences);
    this.#profileReady.set(Boolean(user));
  }

  setEnabled(id: PatrolCommunitySourceId, enabled: boolean): void {
    const next = new Set(this.#enabled());
    if (enabled) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.#enabled.set(next);
    void this.#persist(next);
  }

  async #ensurePatrolProfileLoaded(): Promise<void> {
    await this.#patrolUserService.refetch();

    let attempts = 0;
    while (this.#patrolUserService.isPending() && attempts < 100) {
      await new Promise((resolve) => setTimeout(resolve, 25));
      attempts += 1;
    }
  }

  #applyPreferences(
    preferences: Record<string, string> | undefined,
  ): void {
    this.#enabled.set(
      parsePatrolEnabledSources(preferences?.[PATROL_ENABLED_SOURCES_PREF_KEY]),
    );
  }

  async #persist(sources: ReadonlySet<PatrolCommunitySourceId>): Promise<void> {
    if (
      !this.#authService.isAuthenticated() ||
      !this.#subdomain.isPatrolSubdomain()
    ) {
      return;
    }

    try {
      await lastValueFrom(
        this.#http
          .patch(`${environment.baseUrl}/user/settings`, {
            preferences: {
              [PATROL_ENABLED_SOURCES_PREF_KEY]:
                serializePatrolEnabledSources(sources),
            },
          })
          .pipe(take(1)),
      );
      await this.#queryClient.invalidateQueries({
        queryKey: ['patrol', 'profile'],
      });
    } catch {
      // Keep optimistic local state; profile refetch on next load will reconcile.
    }
  }
}
